import React, { useContext as useContextCore, useCallback, useState, useMemo } from 'react'
import type { TaskStatusEnum } from '@ztechapps/common.contract'
import { useError } from '@ztechapps/common.hook'
import { useTaskStateTransition } from '@ztechapps/task.common'
import { useTaskFilterBarContext } from '../TaskFilterBar/Context'

type OptimisticTransition = {
  taskId: string
  fromStatus: TaskStatusEnum
  toStatus: TaskStatusEnum
}

export type TaskBoardContextType = {
  onStatusChange: (taskId: string, fromStatus: TaskStatusEnum, targetStatus: TaskStatusEnum) => void
  optimisticTransitions: OptimisticTransition[]
  transitioningTaskIds: Set<string>
  collapsedGroups: Record<string, boolean>
  toggleGroup: (key: string) => void
  expandGroup: (key: string) => void
  canEdit: boolean
}

const context = React.createContext<TaskBoardContextType>({} as TaskBoardContextType)

export type TaskBoardProviderProps = {
  children: React.ReactNode
  refetchQueries?: string[]
  canEdit?: boolean
}

export const TaskBoardProvider = ({
  children,
  refetchQueries: refetchQueriesProp,
  canEdit = true,
}: TaskBoardProviderProps) => {
  const { filterState } = useTaskFilterBarContext()
  const resolvedRefetchQueries = refetchQueriesProp ?? (filterState ? ['getTaskStatusCounts'] : [])
  const [actions] = useTaskStateTransition({
    options: {
      mutationOptions: {
        refetchQueries: resolvedRefetchQueries,
        awaitRefetchQueries: true,
      },
    },
  })
  const [transitions, setTransitions] = useState<OptimisticTransition[]>([])
  const [error, setError] = useState<Error | null>(null)
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({})
  useError(error)

  const transitioningTaskIds = useMemo(() => new Set(transitions.map((t) => t.taskId)), [transitions])

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const expandGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: false }))
  }, [])

  const onStatusChange = useCallback(
    async (taskId: string, fromStatus: TaskStatusEnum, targetStatus: TaskStatusEnum) => {
      const transition: OptimisticTransition = { taskId, fromStatus, toStatus: targetStatus }
      setTransitions((prev) => [...prev, transition])
      setError(null)

      try {
        await actions.changeStatus(targetStatus, taskId)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to update task status'))
      } finally {
        setTransitions((prev) => prev.filter((t) => t.taskId !== taskId))
      }
    },
    [actions]
  )

  return (
    <context.Provider
      value={{
        onStatusChange,
        optimisticTransitions: transitions,
        transitioningTaskIds,
        collapsedGroups,
        toggleGroup,
        expandGroup,
        canEdit,
      }}
    >
      {children}
    </context.Provider>
  )
}

export const useTaskBoardContext = () => useContextCore(context)
